import { gql, type TypedDocumentNode } from "@apollo/client";
import {
  PERSON_DETAILS_HEADER_EDUCATION_QUERY,
  PERSON_DETAILS_HEADER_MEMBERSHIP_QUERY
} from "@/features/person/queries";
import {
  GET_CAN_REAFFILIATE_UNIVERSITY_STUDENT_QUERY,
  GET_PGM_PROGRAM_HISTORY_QUERY
} from "@/pages/people/[pgaId]/education/queries";
import { GET_PGM_PROGRAM_TABLE_DATA_QUERY } from "./queries";

export type DeleteEducationProgramRequirementMutationResponse = {
  deleteEducationProgramRequirement: {
    success: boolean;
    message: string;
    programRequirement: {
      requireId: string;
      levelCode: string;
    } | null;
  };
};

export type DeleteEducationProgramRequirementMutationVariables = {
  input: {
    pgaId: string;
    requireId: string;
    levelCode: string;
  };
};

export const DELETE_EDUCATION_PROGRAM_REQUIREMENT_MUTATION: TypedDocumentNode<
  DeleteEducationProgramRequirementMutationResponse,
  DeleteEducationProgramRequirementMutationVariables
> = gql`
  mutation DeleteEducationProgramRequirement($input: BackofficeEducationDeleteEducationProgramRequirementInput!) {
    deleteEducationProgramRequirement(input: $input) {
      success
      message
      programRequirement {
        requireId
        levelCode
      }
    }
  }
`;

export type UpdateEducationProgramRequirementResponse = {
  updateEducationProgramRequirement: {
    success: boolean;
    message: string;
    programRequirement?: {
      requireId: string;
      levelCode: string;
      completionStatus?: string | null;
      completionDate?: string | null;
      score?: number | null;
      remarks?: string | null;
    } | null;
  };
};

export type UpdateEducationProgramRequirementVariables = {
  input: {
    pgaId: string;
    requireId: string;
    levelCode: string;
    completionDate?: string | null;
    completionStatus?: string | null;
    score?: number | null;
    remarks?: string | null;
  };
};

export const UPDATE_EDUCATION_PROGRAM_REQUIREMENT_MUTATION: TypedDocumentNode<
  UpdateEducationProgramRequirementResponse,
  UpdateEducationProgramRequirementVariables
> = gql`
  mutation UpdateEducationProgramRequirement($input: BackofficeEducationUpdateEducationProgramRequirementInput!) {
    updateEducationProgramRequirement(input: $input) {
      success
      message
      programRequirement {
        requireId
        levelCode
        completionStatus
        completionDate
        score
        remarks
      }
    }
  }
`;

export function pgmProgramTableRefetchQueries(pgaId: string) {
  return [
    { query: GET_PGM_PROGRAM_TABLE_DATA_QUERY, variables: { pgaId, includeArchived: false } },
    { query: GET_PGM_PROGRAM_TABLE_DATA_QUERY, variables: { pgaId, includeArchived: true } }
  ];
}

export function educationProgramLevelRefetchQueries(pgaId: string) {
  return [...pgmProgramTableRefetchQueries(pgaId), { query: GET_PGM_PROGRAM_HISTORY_QUERY, variables: { pgaId } }];
}

export function reaffiliateUniversityStudentRefetchQueries(pgaId: string) {
  return [
    ...educationProgramLevelRefetchQueries(pgaId),
    { query: GET_CAN_REAFFILIATE_UNIVERSITY_STUDENT_QUERY, variables: { pgaId } },
    { query: PERSON_DETAILS_HEADER_MEMBERSHIP_QUERY, variables: { pgaId } },
    { query: PERSON_DETAILS_HEADER_EDUCATION_QUERY, variables: { pgaId } }
  ];
}

export type DeleteEducationProgramLevelResponse = {
  deleteEducationProgramLevel: {
    success: boolean;
    message: string;
    pgmProgram?: {
      levelCode: string;
      startDate: string;
    } | null;
  };
};

export type DeleteEducationProgramLevelVariables = {
  input: {
    pgaId: string;
    levelCode: string;
    startDate: string;
  };
};

export const DELETE_EDUCATION_PROGRAM_LEVEL_MUTATION: TypedDocumentNode<
  DeleteEducationProgramLevelResponse,
  DeleteEducationProgramLevelVariables
> = gql`
  mutation DeleteEducationProgramLevel($input: BackofficeEducationDeleteEducationProgramLevelInput!) {
    deleteEducationProgramLevel(input: $input) {
      success
      message
      pgmProgram {
        levelCode
        startDate
      }
    }
  }
`;

export type CreateEducationProgramLevelResponse = {
  createEducationProgramLevel: {
    success: boolean;
    message: string;
    pgmProgram?: {
      levelCode: string;
      startDate: string;
      programType?: string | null;
    } | null;
  };
};

export type CreateEducationProgramLevelVariables = {
  input: {
    pgaId: string;
    levelCode: string;
    startDate: string;
  };
};

export const CREATE_EDUCATION_PROGRAM_LEVEL_MUTATION: TypedDocumentNode<
  CreateEducationProgramLevelResponse,
  CreateEducationProgramLevelVariables
> = gql`
  mutation CreateEducationProgramLevel($input: BackofficeEducationCreateEducationProgramLevelInput!) {
    createEducationProgramLevel(input: $input) {
      success
      message
      pgmProgram {
        levelCode
        startDate
        programType
      }
    }
  }
`;

export type UpdateEducationProgramLevelResponse = {
  updateEducationProgramLevel: {
    success: boolean;
    message: string;
    pgmProgram?: {
      levelCode: string;
      startDate: string;
      endDate?: string | null;
      completeCode?: string | null;
      remarks?: string | null;
      completed?: boolean;
    } | null;
  };
};

export type UpdateEducationProgramLevelVariables = {
  input: {
    pgaId: string;
    levelCode: string;
    originalStartDate: string;
    startDate: string;
    endDate?: string | null;
    completeCode?: string | null;
    remarks?: string | null;
  };
};

export const UPDATE_EDUCATION_PROGRAM_LEVEL_MUTATION: TypedDocumentNode<
  UpdateEducationProgramLevelResponse,
  UpdateEducationProgramLevelVariables
> = gql`
  mutation UpdateEducationProgramLevel($input: BackofficeEducationUpdateEducationProgramLevelInput!) {
    updateEducationProgramLevel(input: $input) {
      success
      message
      pgmProgram {
        levelCode
        startDate
        endDate
        completeCode
        remarks
        completed
      }
    }
  }
`;

export type ArchiveEducationProgramLevelResponse = {
  archiveEducationProgramLevel: {
    success: boolean;
    message: string;
    code: string;
  };
};

export type ArchiveEducationProgramLevelVariables = {
  input: {
    pgaId: string;
    levelCode: string;
  };
};

export const ARCHIVE_EDUCATION_PROGRAM_LEVEL_MUTATION: TypedDocumentNode<
  ArchiveEducationProgramLevelResponse,
  ArchiveEducationProgramLevelVariables
> = gql`
  mutation ArchiveEducationProgramLevel($input: BackofficeEducationArchiveEducationProgramLevelInput!) {
    archiveEducationProgramLevel(input: $input) {
      success
      message
      code
    }
  }
`;

export type PreviewReaffiliateUniversityStudentResponse = {
  previewReaffiliateUniversityStudent: {
    success: boolean;
    message: string;
    code: string;
    canProceed: boolean;
    errors: string[];
    warnings: string[];
    plannedActions: string[];
    cohortName?: string | null;
  };
};

export type PreviewReaffiliateUniversityStudentVariables = {
  input: {
    pgaId: string;
    universityLegacyId: string;
    levelCode: string;
    startDate: string;
    cohortId?: string | null;
  };
};

export const PREVIEW_REAFFILIATE_UNIVERSITY_STUDENT_MUTATION: TypedDocumentNode<
  PreviewReaffiliateUniversityStudentResponse,
  PreviewReaffiliateUniversityStudentVariables
> = gql`
  mutation PreviewReaffiliateUniversityStudent($input: BackofficeEducationReaffiliateUniversityStudentInput!) {
    previewReaffiliateUniversityStudent(input: $input) {
      success
      message
      code
      canProceed
      errors
      warnings
      plannedActions
      cohortName
    }
  }
`;

export type ReaffiliateUniversityStudentResponse = {
  reaffiliateUniversityStudent: {
    success: boolean;
    message: string;
    code: string;
    cohort?: {
      id: string;
      name: string;
    } | null;
  };
};

export type ReaffiliateUniversityStudentVariables = {
  input: {
    pgaId: string;
    universityLegacyId: string;
    levelCode: string;
    startDate: string;
    cohortId?: string | null;
  };
};

export const REAFFILIATE_UNIVERSITY_STUDENT_MUTATION: TypedDocumentNode<
  ReaffiliateUniversityStudentResponse,
  ReaffiliateUniversityStudentVariables
> = gql`
  mutation ReaffiliateUniversityStudent($input: BackofficeEducationReaffiliateUniversityStudentInput!) {
    reaffiliateUniversityStudent(input: $input) {
      success
      message
      code
      cohort {
        id
        name
      }
    }
  }
`;
